import type { ReactNode } from 'react';
import { EyeOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';

import type { FeConfig } from '../../types/config';
import type { EventFieldConfig } from '../eventsConfig';
import type { PromoFieldConfig } from '../promoConfig';
import type { FormCascadeRule } from '../formCascadeConfig';
import type { FieldRule } from '../fieldRulesConfig';
import type { PlanFieldConfig } from '../planConfig';
import type { PlanIndexColumnConfig } from '../planIndexColumnsConfig';
import type { CalendarColumnConfig } from '../calendarColumnsConfig';
import type { RowActionConfig } from '../calendarMenus';
import type { UpliftConfig } from '../upliftConfig';
import type { Labels } from '../labels';
import type { OptimizePlanConfig } from '../optimizePlanConfig';
import type { OptimizeResultConfig } from '../optimizeResultConfig';
import type { ImportEventFromPlanConfig } from '../importEventFromPlanConfig';
import type { MetricPageConfig } from '../metricPageConfig';

export interface LabeledOption {
    label: string;
    value: string;
}

export interface FilterKeyConfig {
    label: string;
    value: string;
    dimension: string;
}

export interface FilterDimensionInfo {
    [dimension: string]: { label: string; key: string };
}

/**
 * The portion of `TpoGenConfig` sourced from `fe_config` (everything except the
 * metric-view-derived `promoMetricsConfig` / `promoSpendsConfig`).
 */
export interface FeDerivedConfig {
    showEditableIndicators: boolean;
    eventDetailsFields: EventFieldConfig[];
    promoDetailsFields: EventFieldConfig[];
    promoFields: PromoFieldConfig[];
    upliftConfig: UpliftConfig;
    formCascadeRules: FormCascadeRule[];
    fieldRules: FieldRule[];
    metricRules: FieldRule[];
    planFields: PlanFieldConfig[];
    planIndexColumns: PlanIndexColumnConfig[];
    calendarIntervalOptions: LabeledOption[];
    allDefaultColumns: CalendarColumnConfig[];
    defaultRowActionItems: RowActionConfig;
    planActionMenuItems: MenuProps['items'];
    labels: Labels;
    planIndexFilterKeys: FilterKeyConfig[];
    filterDimensionInfo: FilterDimensionInfo;
    calendarFilterKeys: FilterKeyConfig[];
    calendarFilterDimensionInfo: FilterDimensionInfo;
    calendarGroupByOptions: LabeledOption[];
    metricPageConfig: MetricPageConfig;
    optimizePlanConfig: OptimizePlanConfig;
    optimizeResultConfig: OptimizeResultConfig;
    importEventFromPlanConfig: ImportEventFromPlanConfig;
}

// Menu item icons arrive as string identifiers (JSON cannot carry React nodes).
// Map the known ones back to elements; unknown identifiers fall through as
// `undefined` (tolerant: a missing icon must not block the module).
const ICON_BY_NAME: Record<string, ReactNode> = {
    EyeOutlined: <EyeOutlined />,
};

type RawMenuItem = Record<string, unknown>;

const resolveMenuItems = (items: RawMenuItem[]): MenuProps['items'] =>
    items.map((item) => {
        if (item.type === 'divider') return { type: 'divider' };
        const { icon, ...rest } = item;
        const resolvedIcon = typeof icon === 'string' ? ICON_BY_NAME[icon] : undefined;
        return resolvedIcon ? { ...rest, icon: resolvedIcon } : rest;
    }) as unknown as MenuProps['items'];

const toCalendarColumns = (cols: Record<string, unknown>[]): CalendarColumnConfig[] =>
    // The draft drops `required`; default it to false (no force-locked column).
    cols.map((col) => ({ required: false, ...col })) as unknown as CalendarColumnConfig[];

/**
 * Maps the validated backend `fe_config` to the FE static-config shape. The FE
 * TS types remain the authoritative contract: the backend `fe_config` was
 * derived from them, so each section is cast field-for-field, with two
 * JSON-driven fix-ups (icon identifiers -> nodes, defaulted `required`).
 */
export function adaptFeConfig(fe: FeConfig): FeDerivedConfig {
    return {
        showEditableIndicators: fe.showEditableIndicators,
        eventDetailsFields: fe.eventDetailsFields as unknown as EventFieldConfig[],
        promoDetailsFields: fe.promoDetailsFields as unknown as EventFieldConfig[],
        promoFields: fe.promoFields as unknown as PromoFieldConfig[],
        upliftConfig: fe.upliftConfig as unknown as UpliftConfig,
        formCascadeRules: (fe.formCascadeRules ?? []) as unknown as FormCascadeRule[],
        fieldRules: (fe.fieldRules ?? []) as unknown as FieldRule[],
        metricRules: (fe.metricRules ?? []) as unknown as FieldRule[],
        planFields: fe.planFields as unknown as PlanFieldConfig[],
        planIndexColumns: fe.planIndexColumns as unknown as PlanIndexColumnConfig[],
        calendarIntervalOptions: fe.calendarIntervalOptions as unknown as LabeledOption[],
        allDefaultColumns: toCalendarColumns(fe.allDefaultColumns),
        defaultRowActionItems: fe.defaultRowActionItems as unknown as RowActionConfig,
        planActionMenuItems: resolveMenuItems(fe.planActionMenuItems),
        labels: fe.labels as unknown as Labels,
        planIndexFilterKeys: fe.planIndexFilterKeys as unknown as FilterKeyConfig[],
        filterDimensionInfo: fe.filterDimensionInfo as unknown as FilterDimensionInfo,
        calendarFilterKeys: fe.calendarFilterKeys as unknown as FilterKeyConfig[],
        calendarFilterDimensionInfo: fe.calendarFilterDimensionInfo as unknown as FilterDimensionInfo,
        calendarGroupByOptions: fe.calendarGroupByOptions as unknown as LabeledOption[],
        metricPageConfig: fe.metricPageConfig as unknown as MetricPageConfig,
        optimizePlanConfig: fe.optimizePlanConfig as unknown as OptimizePlanConfig,
        optimizeResultConfig: fe.optimizeResultConfig as unknown as OptimizeResultConfig,
        importEventFromPlanConfig: fe.importEventFromPlanConfig as unknown as ImportEventFromPlanConfig,
    };
}
